import {inject, Injectable, NgZone} from '@angular/core';
import {Router} from '@angular/router';
import {LocalNotifications} from '@capacitor/local-notifications';
import type {LocalNotificationSchema} from '@capacitor/local-notifications';
import {LoggerService} from '../logging/logger.service';
import {AppConfigService} from '../../config/app-config.service';
import {AuthService} from '../auth/auth.service';

/** Stable notification IDs — never change these */
const NOTIFICATION_IDS = {
  WEEKDAY_MON: 101,
  WEEKDAY_TUE: 102,
  WEEKDAY_WED: 103,
  WEEKDAY_THU: 104,
  WEEKDAY_FRI: 105,
  WEEKLY_REPORT: 201,
  MONTHLY_REPORT: 301,
  IMMEDIATE_BASE: 401,
} as const;

const ALL_NOTIFICATION_IDS = Object.values(NOTIFICATION_IDS);

/** ScheduleOn weekday values (1=Sun, 2=Mon ... 7=Sat) */
const WEEKDAY = {
  MONDAY: 2,
  TUESDAY: 3,
  WEDNESDAY: 4,
  THURSDAY: 5,
  FRIDAY: 6,
} as const;

const ANDROID_CHANNEL_ID = 'hec-reminders';

@Injectable({providedIn: 'root'})
export class NotificationService {
  private readonly logger = inject(LoggerService);
  private readonly appConfig = inject(AppConfigService);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly authService = inject(AuthService);

  private initialised = false;
  private immediateCounter = 0;

  async initialise(): Promise<void> {
    if (!this.appConfig.isMobile) {
      this.logger.debug('Notifications: skipping, not mobile');
      return;
    }

    this.registerActionListener();

    this.authService.loggedInUser$.subscribe(async (user) => {
      if (user && !this.initialised) {
        this.initialised = true;
        const granted = await this.requestPermission();
        if (granted) {
          await this.ensureAndroidChannel();
          await this.scheduleAllNotifications();
        }
      }
      if (!user && this.initialised) {
        await this.cancelAll();
        this.initialised = false;
      }
    });
  }

  private async requestPermission(): Promise<boolean> {
    const status = await LocalNotifications.checkPermissions();
    if (status.display === 'granted') {
      return true;
    }
    const result = await LocalNotifications.requestPermissions();
    const granted = result.display === 'granted';
    this.logger.info(
      'Notifications: permission',
      granted ? 'granted' : 'denied'
    );
    return granted;
  }

  private async ensureAndroidChannel(): Promise<void> {
    if (!this.appConfig.isAndroid) return;

    await LocalNotifications.createChannel({
      id: ANDROID_CHANNEL_ID,
      name: 'Reminders',
      description: 'Activity and summary reminders',
      importance: 3,
    });
    this.logger.debug('Notifications: Android channel created');
  }

  private async scheduleAllNotifications(): Promise<void> {
    await this.cancelAll();

    const notifications = [
      ...this.buildWeekdayReminders(),
      this.buildWeeklyReport(),
      this.buildMonthlyReport()
    ];

    await LocalNotifications.schedule({notifications});
    this.logger.info(
      'Notifications: scheduled',
      notifications.length,
      'notifications'
    );
  }

  private buildWeekdayReminders(): LocalNotificationSchema[] {
    const weekdays = [
      {id: NOTIFICATION_IDS.WEEKDAY_MON, weekday: WEEKDAY.MONDAY},
      {id: NOTIFICATION_IDS.WEEKDAY_TUE, weekday: WEEKDAY.TUESDAY},
      {id: NOTIFICATION_IDS.WEEKDAY_WED, weekday: WEEKDAY.WEDNESDAY},
      {id: NOTIFICATION_IDS.WEEKDAY_THU, weekday: WEEKDAY.THURSDAY},
      {id: NOTIFICATION_IDS.WEEKDAY_FRI, weekday: WEEKDAY.FRIDAY},
    ];

    return weekdays.map(({id, weekday}) => ({
      id,
      title: 'Log today\'s activities',
      body: 'What did you learn today? Tap to record.',
      schedule: {
        on: {weekday, hour: 19, minute: 0},
        allowWhileIdle: true,
      },
      channelId: ANDROID_CHANNEL_ID,
      extra: {deepLink: '/tabs/events'},
    }));
  }

  private buildWeeklyReport(): LocalNotificationSchema {
    return {
      id: NOTIFICATION_IDS.WEEKLY_REPORT,
      title: 'Weekly learning summary',
      body: 'Your weekly summary is ready. Tap to view.',
      schedule: {
        on: {weekday: WEEKDAY.MONDAY, hour: 9, minute: 0},
        allowWhileIdle: true,
      },
      channelId: ANDROID_CHANNEL_ID,
      extra: {deepLink: '/tabs/reports'},
    };
  }

  private buildMonthlyReport(): LocalNotificationSchema {
    return {
      id: NOTIFICATION_IDS.MONTHLY_REPORT,
      title: 'Monthly learning summary',
      body: 'Your monthly summary is ready. Tap to view.',
      schedule: {
        on: {day: 1, hour: 9, minute: 0},
        allowWhileIdle: true,
      },
      channelId: ANDROID_CHANNEL_ID,
      extra: {deepLink: '/tabs/reports'},
    };
  }

  private registerActionListener(): void {
    LocalNotifications.addListener(
      'localNotificationActionPerformed',
      (action) => {
        const deepLink = action.notification.extra?.deepLink;
        if (deepLink) {
          this.logger.info(
            'Notifications: tapped, navigating to', deepLink
          );
          this.zone.run(() => {
            this.router.navigateByUrl(deepLink);
          });
        }
      }
    );
  }

  async sendImmediate(
    title: string,
    body: string,
    deepLink?: string
  ): Promise<void> {
    if (!this.appConfig.isMobile) return;

    const id = NOTIFICATION_IDS.IMMEDIATE_BASE
      + this.immediateCounter++;

    await LocalNotifications.schedule({
      notifications: [{
        id,
        title,
        body,
        schedule: {at: new Date(Date.now() + 1000)},
        channelId: ANDROID_CHANNEL_ID,
        ...(deepLink ? {extra: {deepLink}} : {}),
      }]
    });
    this.logger.debug('Notifications: scheduled immediate', id);
  }

  private async cancelAll(): Promise<void> {
    try {
      await LocalNotifications.cancel({
        notifications: ALL_NOTIFICATION_IDS.map(id => ({id}))
      });
      this.logger.debug('Notifications: cancelled all');
    } catch (error) {
      this.logger.warn('Notifications: error cancelling', error);
    }
  }
}
